import { ActionIcon, Badge, Box, Button, Group, Menu, ScrollArea, Stack, Text } from '@mantine/core'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { LuBell } from 'react-icons/lu'
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
} from '@/frontend/hooks/useNotifications'

export function NotificationBell() {
  const { t } = useTranslation()
  const { data } = useNotifications()
  const items = data?.items ?? []
  const unread = data?.unreadCount ?? 0
  const markRead = useMarkNotificationRead()
  const markAll = useMarkAllNotificationsRead()

  return (
    <Menu
      shadow="md"
      width={360}
      /* "right-end" used to flip open at 360px wide and overflow past the
         left viewport edge on phones (the bell sits inside the sidebar).
         "bottom-end" anchors to the bell's right edge and grows downward,
         so the dropdown stays inside the viewport on every screen size.
         Combined with maxWidth: calc(100vw - 16px) on the dropdown styles
         below, the panel is also clipped to viewport on very narrow phones. */
      position="bottom-end"
      withArrow
      zIndex={1100}
      withinPortal
    >
      <Menu.Target>
        <ActionIcon
          variant="subtle"
          color="gray"
          size="lg"
          aria-label={t('component.notification.title')}
          style={{ position: 'relative' }}
        >
          <LuBell size={18} />
          {unread > 0 && (
            <Badge
              size="xs"
              color="red"
              circle
              style={{ position: 'absolute', top: 2, right: 2, padding: 0, minWidth: 16, height: 16 }}
            >
              {unread}
            </Badge>
          )}
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown p={0} style={{ maxWidth: 'calc(100vw - 16px)' }}>
        <Group justify="space-between" p="sm" style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }}>
          <Text fw={600} size="sm">
            {t('component.notification.title')}
          </Text>
          {unread > 0 && (
            <Button variant="subtle" size="compact-xs" onClick={() => markAll.mutate()}>
              {t('component.notification.mark_all')}
            </Button>
          )}
        </Group>
        <ScrollArea.Autosize mah={360}>
          {items.length === 0 && (
            <Stack align="center" p="xl">
              <Text size="sm" c="dimmed">
                {t('component.notification.empty')}
              </Text>
            </Stack>
          )}
          {items.map((n) => (
            <Box
              key={n.id}
              p="sm"
              style={{
                borderBottom: '1px solid var(--mantine-color-default-border)',
                background: n.readAt ? undefined : 'var(--mantine-color-teal-0)',
                cursor: n.linkUrl ? 'pointer' : 'default',
              }}
              onClick={() => {
                if (!n.readAt) markRead.mutate(n.id)
                if (n.linkUrl) window.location.href = n.linkUrl
              }}
            >
              <Group justify="space-between" wrap="nowrap" gap="xs">
                <Text size="sm" fw={600} truncate style={{ flex: 1 }}>
                  {n.title}
                </Text>
                <Text size="xs" c="dimmed">
                  {dayjs(n.createdAt).format('DD MMM HH:mm')}
                </Text>
              </Group>
              {n.body && (
                <Text size="xs" c="dimmed" mt={2} lineClamp={2}>
                  {n.body}
                </Text>
              )}
            </Box>
          ))}
        </ScrollArea.Autosize>
      </Menu.Dropdown>
    </Menu>
  )
}
